/**
 * ResidentProfilePage — V2 orchestrator for the Resident profile.
 *
 * Manages:
 *   - Session & permissions (EHR-aware)
 *   - Two polling cycles (basic data 600s, reporting data 1200s)
 *   - Tab state (basic, reporting, devices, wellness, logs)
 *   - Drawer/dialog state for all edit flows
 *   - Lifecycle actions (relocate, move-out, move-in, delete)
 *   - Toast notifications
 *
 * Props:
 *   id — Resident ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import './resident-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import EmergencyContactDrawer from './components/EmergencyContactDrawer.jsx';
import PhotoUploadDialog from './components/PhotoUploadDialog.jsx';
import ReportingTab from './components/ReportingTab.jsx';
import DevicesTab from './components/DevicesTab.jsx';
import DeviceAssignDrawer from './components/DeviceAssignDrawer.jsx';
import EasyAddDeviceDrawer from '../../../../components/v2/EasyAddDeviceDrawer.jsx';
import DeviceRemoveDialog from './components/DeviceRemoveDialog.jsx';
import WellnessTab from './components/WellnessTab.jsx';
import LogsTab from './components/LogsTab.jsx';
import TrendHistoryTab from './components/TrendHistoryTab.jsx';
import { fetchResidentEpisodes } from '../../../../components/resident-trends/episodesApi.js';
import EventNoteDialog from './components/EventNoteDialog.jsx';
import RelocateDialog from '../components/RelocateDialog.jsx';
import MoveOutDialog from '../components/MoveOutDialog.jsx';
import MoveInDialog from './components/MoveInDialog.jsx';
import DeleteDialog from './components/DeleteDialog.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, updateSessionCampus, getRoleWeight, getOffsiteMonitoringVendors, getActiveVendorIdList, getAllVendors, getCampusConfig, isResidentDataEhrManaged,
} from '../../../../services/session.js';
import {
  fetchResidentProfile, fetchResidentDevices, fetchResidentWellness,
  fetchCareflowWindows, fetchWellnessWindows, fetchAreas,
  fetchAvailableDevices,
  fetchResidentLogs, fetchResidentFlag, fetchPreformNotes,
  fetchEmergencyContacts, addEmergencyContact, editEmergencyContact, deleteEmergencyContact,
  fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
  editResidentProfile, uploadResidentPhoto,
  assignDeviceToResident, removeDeviceFromResident,
  relocateResident, moveOutResident, moveInResident, deleteResident,
  addEventNote,
} from './api/residentProfileApi.js';
import { fetchCallLogsReport } from '../../../reports/call-logs/api/callLogsReportApi.js';
import { addDevice } from '../../../devices/api/devicesApi.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  convertIsoToLocal,
  convertIsoUTCToLocal,
  convertIsoUTCToLocalIsoDate,
  formatDateIso,
  shiftDateStr,
  formatPhone,
  getToday,
  getUTCTimestamp,
  setResFilters,
  convertHttpUtcToLocal,
} from '../../../../libs.jsx';
import { fetchInovonicsLocations } from '../api/residentsApi.js';
import stockPhoto from '../../../../img/nophoto.png';

/** Resident Trends episode statuses that count as still open. */
const TREND_OPEN_STATUSES = ['new', 'acknowledged', 'in_progress'];

/**
 * Find this resident's most-recent RTLS location from the Inovonics feed by
 * matching a location entry's txid to one of the resident's device serials
 * (manufacture_uuid). Returns { area_name, area_id, timestamp(local) } or null
 * when there's no usable match (caller keeps the last-known value).
 *
 * @param {Array} locationData — response from fetchInovonicsLocations
 * @param {Array} devices — resident's attached devices
 * @param {number} tzOffset — campus tz_offset (signed hours from UTC)
 */
function matchResidentLocation(locationData, devices, tzOffset) {
  const serials = new Set(
    (devices || [])
      .map((d) => (d?.manufacture_uuid != null ? String(d.manufacture_uuid) : null))
      .filter(Boolean),
  );
  if (serials.size === 0) return null;
  const entry = (locationData || []).find(
    (e) => e?.txid != null && serials.has(String(e.txid)) && e?.location?.area_name,
  );
  if (!entry) return null;
  return {
    area_name: entry.location.area_name,
    area_id: entry.location.area_id,
    timestamp: convertHttpUtcToLocal(entry.location.timestamp, tzOffset),
  };
}

// ==========================================================================
// COMPONENT
// ==========================================================================

const ResidentProfilePage = ({ id }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId.toString()]?.name || s?.campus || '';
  }, [campusId]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);

  const residentId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  // Campus config for the active campus — the one canonical accessor for all
  // campus-specific settings (feature flags, timezone, resident trends) used here.
  const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);

  /* EHR-aware permissions. Uses the shared helper rather than reading
     getEhrInfo().active directly so this page and the residents list enforce one
     definition — they had drifted, and the list page ended up with no gate at
     all (see ResidentsPage `canEditResidents`). Same boolean either way. */
  const ehrActive = useMemo(() => isResidentDataEhrManaged(campusId), [campusId]);

  // RTLS (Real-time Locating) — active vendor 14 on the current campus.
  const locatingActive = useMemo(
    () => getAllVendors().some((v) => v.vendor_active && v.vendor_id === 14),
    [campusId],
  );

  const baseEditRights = useMemo(
    () => (!ehrActive && iAmHigherAuth(10, myRoleWeight)) || iAmHigherAuth(80, myRoleWeight),
    [ehrActive, myRoleWeight]
  );
  const baseAddRights = baseEditRights;
  const baseDeleteRights = baseEditRights;
  const devEditRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const showAdminTabs = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  // Edit rights disabled for inactive residents (computed after data loads)
  const [isActive, setIsActive] = useState(true);
  const editRights = baseEditRights && isActive;
  const addRights = baseAddRights;
  const deleteRights = baseDeleteRights;

  const getSessionParams = useCallback(() => {
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      vendor_ids: getActiveVendorIdList(),
    };
  }, [campusId]);

  // Session feature flags
  const eventNotesActive = campusConfig?.event_notes_active || false;
  const eventNotesPreform = campusConfig?.event_notes_preform || false;
  const eventNotesFreeform = campusConfig?.event_notes_freeform || false;
  const isReasonEditable = campusConfig?.is_reason_editable || false;
  const tzOffset = parseInt(campusConfig?.tz_offset || 0);
  // Grandstream campuses get a Calls column in the Recent Events table (call
  // logs merged into events by event_id, mirroring AlertDetailsPage).
  const isGrandstream = Boolean(campusConfig?.is_grandstream);
  const showTrends = Boolean(campusConfig?.resident_trends);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // TABS
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('basic');

  // --------------------------------------------------------------------------
  // CORE STATE
  // --------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);

  // Basic resident data
  const [basicData, setBasicData] = useState({});
  const [ehrData, setEhrData] = useState({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickName, setNickName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [areaName, setAreaName] = useState('');
  const [areaType, setAreaType] = useState('');
  const [areaId, setAreaId] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const [moveOutDate, setMoveOutDate] = useState('');
  const [resGroups, setResGroups] = useState([]);
  const [areaGroups, setAreaGroups] = useState([]);
  const [ehrId, setEhrId] = useState('');

  // Review flag
  const [flagData, setFlagData] = useState({});
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isReview, setIsReview] = useState(false);
  const [tagColor, setTagColor] = useState('unset');

  // Reference data
  const [locationData, setLocationData] = useState([]);
  const [resWellData, setResWellData] = useState({});

  // Resident Trends episodes (fetched once here, split across the Basic Info
  // panel (open) and the Trend History tab (closed))
  const [trendEpisodes, setTrendEpisodes] = useState([]);
  const [trendMutes, setTrendMutes] = useState([]);

  // Device data
  const [resDeviceData, setResDeviceData] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);

  // RTLS location for this resident ({ area_name, area_id, timestamp } | null).
  // resDeviceRef mirrors the latest devices so the 60s poll can match without
  // resetting its interval on every device-data change.
  const [resLocation, setResLocation] = useState(null);
  const resDeviceRef = useRef([]);

  // Wellness
  const [careData, setCareData] = useState([]);
  const [wellnessWindowData, setWellnessWindowData] = useState([]);

  // Logs
  const [logData, setLogData] = useState([]);

  // Emergency Contacts
  const [emergencyContacts, setEmergencyContacts] = useState([]);

  // Reporting
  const [hourData, setHourData] = useState({});
  const [weekData, setWeekData] = useState({});
  const [reasonData, setReasonData] = useState({});
  const [eventData, setEventData] = useState([]);
  const [prenoteData, setPrenoteData] = useState([]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  // Quick add-and-assign, launched from inside the assign drawer
  const [easyAddOpen, setEasyAddOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeDevice, setRemoveDevice] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteEventIndex, setNoteEventIndex] = useState(null);
  const [relocateDialogOpen, setRelocateDialogOpen] = useState(false);
  const [moveOutDialogOpen, setMoveOutDialogOpen] = useState(false);
  const [moveInDialogOpen, setMoveInDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // DATE RANGE (for event reason summary)
  // --------------------------------------------------------------------------
  const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
  const endDate = useMemo(() => formatDateIso(getToday()), []);

  // --------------------------------------------------------------------------
  // REVIEW FLAG LOGIC
  // --------------------------------------------------------------------------
  function checkCareLevel(rindex, carelevel) {
    if (rindex >= 0.5 && carelevel) {
      if (rindex >= 0.5 && rindex < 1 && ['level 0'].includes(carelevel.toLowerCase())) return true;
      if (rindex >= 1 && rindex < 2 && ['level 0', 'level 1', 'level 2'].includes(carelevel.toLowerCase())) return true;
      if (rindex >= 2 && ['level 0', 'level 1', 'level 2', 'level 3', 'level 4'].includes(carelevel.toLowerCase())) return true;
    }
    return false;
  }

  useEffect(() => {
    if (Object.keys(flagData).length > 0) {
      const rIndex = flagData?.review_index || 0;
      const careLevel = ehrData?.CareLevel || basicData?.ehr_level_of_care || '';
      setIsReview(flagData?.resident_long_stay === 1 && checkCareLevel(rIndex, careLevel));
      setReviewIndex(rIndex);
      if (rIndex > 2) {
        setTagColor('#e63757');
      } else if (rIndex > 1) {
        setTagColor('#ff7815');
      } else if (rIndex > 0.5) {
        setTagColor('#f6c343');
      } else {
        setTagColor('unset');
      }
    }
  }, [flagData, ehrData, basicData]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 1: Basic data (600 second interval)
  // --------------------------------------------------------------------------
  const fetchBasicDataCycle = useCallback(async () => {
    const baseFetches = [
      fetchResidentProfile(campusId, residentId),
      fetchResidentDevices(campusId, residentId),
      fetchResidentWellness(campusId, residentId),
      fetchCareflowWindows(campusId),
      fetchWellnessWindows(campusId),
      fetchAreas(campusId),
      fetchAvailableDevices(campusId),
      showAdminTabs ? fetchResidentLogs(residentId) : Promise.resolve([]),
      fetchResidentFlag(campusId, residentId),
      fetchEmergencyContacts(campusId, residentId),
      // RTLS locations (third-party Inovonics) — only when vendor 14 is active.
      locatingActive
        ? fetchInovonicsLocations(campusId, true).catch((locErr) => {
            console.error('[ResidentProfilePage] fetchInovonicsLocations error:', locErr);
            return [];
          })
        : Promise.resolve(null),
    ];

    const [
      profileResult, devicesResult, wellnessResult,
      careResult, wellnessWinResult, areasResult,
      availResult,
      logsResult, flagResult, contactsResult,
      rtlsResult,
    ] = await Promise.all(baseFetches);

    // Process profile
    if (profileResult?.length > 0) {
      const b = profileResult[0];
      setBasicData(b);
      setEhrData(b?.ehr_data?.length > 0 ? b.ehr_data[0] : {});
      setFirstName(b.resident_first_name || '');
      setLastName(b.resident_last_name || '');
      setNickName(b.resident_nickname || '');
      setPhone(b.resident_phone ? formatPhone(b.resident_phone) : '');
      setEmail(b.resident_email || '');
      setNotes(b.notes || '');
      setAreaName(b.area_name || '');
      setAreaType(b.area_type_description || '');
      setAreaId(b.area_id || 0);
      setImageUrl(b.image_url || '');
      setIsActive(b.is_active ? true : false);
      setMoveInDate(b.move_in_date || '');
      setMoveOutDate(b.move_out_date || '');
      setResGroups(b.groups || []);
      setAreaGroups(b.area_groups || []);
      setEhrId(b.ehr_external_id || '');
    }

    setResDeviceData(devicesResult || []);
    // RTLS: match this resident's device serials to the location feed. Keep the
    // last-known location when this cycle has no usable match (blank reading).
    if (locatingActive) {
      const matched = matchResidentLocation(rtlsResult, devicesResult || [], tzOffset);
      setResLocation((prev) => matched || prev);
    }
    setResWellData(wellnessResult?.length > 0 ? wellnessResult[0] : {});
    setCareData(careResult || []);
    setWellnessWindowData(wellnessWinResult || []);
    setLocationData(areasResult || []);
    setAvailableDevices(availResult || []);
    setLogData(logsResult || []);
    setFlagData(flagResult?.length > 0 ? flagResult[0] : {});
    setEmergencyContacts(contactsResult || []);
    setLoading(false);
  }, [campusId, residentId, showAdminTabs, locatingActive, tzOffset]);

  useEffect(() => {
    fetchBasicDataCycle();
    const interval = setInterval(fetchBasicDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchBasicDataCycle]);

  // Keep a ref of the resident's devices so the RTLS poll can match serials
  // without re-creating its interval whenever device data refreshes.
  useEffect(() => { resDeviceRef.current = resDeviceData; }, [resDeviceData]);

  // Poll RTLS locations every 60s (last-known preserved when a poll is blank).
  useEffect(() => {
    if (!locatingActive) return undefined;
    const interval = setInterval(async () => {
      try {
        const locationData = await fetchInovonicsLocations(campusId, true);
        const matched = matchResidentLocation(locationData, resDeviceRef.current, tzOffset);
        setResLocation((prev) => matched || prev);
      } catch (err) {
        console.error('[ResidentProfilePage] RTLS location poll error:', err);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [locatingActive, campusId, tzOffset]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 2: Reporting data (1200 second interval)
  // --------------------------------------------------------------------------
  const fetchReportingCycle = useCallback(async () => {
    const [hourResult, weekResult, reasonResult, eventsResult, prenotes] = await Promise.all([
      fetchHourChart(campusId, residentId),
      fetchWeekChart(campusId, residentId),
      fetchReasonSummary(campusId, residentId, startDate, endDate),
      fetchLimitedEvents(campusId, residentId),
      (eventNotesPreform ? fetchPreformNotes(campusId) : Promise.resolve([])),
    ]);

    let events = eventsResult || [];

    // Grandstream campuses: fetch call logs spanning the recent events window
    // and merge them into each event row by event_id (mirrors AlertDetailsPage)
    // so the Recent Events table can show a per-event Calls badge. Non-fatal on
    // failure — the events still render, just without call badges.
    if (isGrandstream && events.length) {
      try {
        const dates = events
          .map((e) => (e.event_datetime_local ? String(e.event_datetime_local).slice(0, 10) : ''))
          .filter((d) => d.length === 10)
          .sort();
        if (dates.length) {
          const callResp = await fetchCallLogsReport({
            campusId,
            startDate: shiftDateStr(dates[0], -1),
            endDate: shiftDateStr(dates[dates.length - 1], 1),
          });
          const callRecords = callResp?.records || [];
          if (callRecords.length) {
            const callsByEvent = new Map();
            callRecords.forEach((c) => {
              const key = String(c.event_id ?? '');
              if (!key) return;
              if (!callsByEvent.has(key)) callsByEvent.set(key, []);
              callsByEvent.get(key).push(c);
            });
            callsByEvent.forEach((arr) => arr.sort(
              (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
            ));
            events = events.map((r) => ({
              ...r,
              calls: callsByEvent.get(String(r.event_id ?? '')) || [],
            }));
          }
        }
      } catch (err) {
        console.error('[ResidentProfilePage] Call logs merge failed:', err);
      }
    }

    setHourData(hourResult || {});
    setWeekData(weekResult || {});
    setReasonData(reasonResult || {});
    setEventData(events);
    setPrenoteData(prenotes || []);
    setReportLoading(false);
  }, [campusId, residentId, startDate, endDate, eventNotesPreform, isGrandstream]);

  useEffect(() => {
    fetchReportingCycle();
    const interval = setInterval(fetchReportingCycle, 1_200_000);
    return () => clearInterval(interval);
  }, [fetchReportingCycle]);

  // --------------------------------------------------------------------------
  // CAMPUS CHANGE HANDLER
  // --------------------------------------------------------------------------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    updateSessionCampus(val);
    navigate('/community/residents');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  /** Save basic info */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();

    // Move-in date UTC handling
    let movein = formData.moveInDate;
    if (movein && ![null, undefined, '', '0000-00-00 00:00:00'].includes(movein)) {
      const hourOfDay = Math.min(23, Math.max(8, Math.abs(tzOffset) + 8));
      if (moveInDate !== '') {
        const tmpTime = moveInDate.split(' ')[1];
        movein = tmpTime ? movein.toString() + ` ${tmpTime}` : movein.toString() + ` ${hourOfDay}:00:00`;
      } else {
        movein = movein.toString() + ` ${hourOfDay}:00:00`;
      }
    } else {
      movein = null;
    }

    const payload = [{
      resident_id: residentId,
      campus_id: campusId,
      resident_first_name: formData.firstName,
      resident_last_name: formData.lastName,
      resident_nickname: formData.nickName,
      resident_phone: formData.phone.replaceAll('-', ''),
      resident_email: formData.email.toLowerCase(),
      notes: formData.notes,
      ehr_external_id: formData.ehrId,
      move_in_date: movein,
      modified_by: session.userId,
      vendor_ids: session.vendor_ids,
    }];
    const oldPayload = [{
      resident_id: residentId,
      campus_id: campusId,
      resident_first_name: firstName,
      resident_last_name: lastName,
      resident_nickname: nickName,
      resident_phone: phone.replaceAll('-', ''),
      resident_email: email,
      notes,
      ehr_external_id: ehrId,
      move_in_date: moveInDate,
    }];

    const result = await editResidentProfile(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Resident information saved.');
      setBasicDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, campusId, firstName, lastName, nickName, phone, email, notes, ehrId, moveInDate, tzOffset, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Upload profile photo */
  const handlePhotoUpload = useCallback(async (fileData, fileExt) => {
    setSaving(true);
    const session = getSessionParams();

    let oldFilename = '';
    if (basicData?.image_url) {
      const oldURL = basicData.image_url.split('/');
      oldFilename = oldURL.slice(-1)[0];
    }

    const payload = {
      image_file: fileData,
      filename: `${uuidv4()}.${fileExt}`,
      resident_id: residentId,
      campus_id: campusId,
      oldfilename: oldFilename,
    };

    const result = await uploadResidentPhoto(payload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Photo uploaded successfully.');
      setPhotoDialogOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during upload${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, campusId, basicData, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Add or edit an emergency contact */
  const handleSaveContact = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();
    const isEdit = !!formData.monitoring_contact_id;

    let result;
    if (isEdit) {
      // Find the original contact for oldPayload
      const oldContact = emergencyContacts.find(
        (c) => c.monitoring_contact_id === formData.monitoring_contact_id,
      );
      const payload = [{
        monitoring_contact_id: formData.monitoring_contact_id,
        campus_id: campusId,
        resident_id: parseInt(residentId),
        contact_first_name: formData.contact_first_name,
        contact_last_name: formData.contact_last_name,
        contact_name: formData.contact_name,
        relationship: formData.relationship,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        modified_by: session.userId,
      }];
      const oldPayload = oldContact ? [{
        monitoring_contact_id: oldContact.monitoring_contact_id,
        campus_id: oldContact.campus_id,
        resident_id: oldContact.resident_id,
        contact_first_name: oldContact.contact_first_name,
        contact_last_name: oldContact.contact_last_name,
        contact_name: oldContact.contact_name,
        relationship: oldContact.relationship,
        contact_phone: oldContact.contact_phone,
        contact_email: oldContact.contact_email,
      }] : [];
      result = await editEmergencyContact(payload, oldPayload, session);
    } else {
      const payload = [{
        campus_id: campusId,
        resident_id: parseInt(residentId),
        contact_first_name: formData.contact_first_name,
        contact_last_name: formData.contact_last_name,
        contact_name: formData.contact_name,
        relationship: formData.relationship,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        is_active: 1,
        modified_by: session.userId,
      }];
      result = await addEmergencyContact(payload, session);
    }

    setSaving(false);
    if (result && result[0]) {
      showToast('success', isEdit ? 'Emergency contact updated.' : 'Emergency contact added.');
      setContactDrawerOpen(false);
      setEditingContact(null);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to ${isEdit ? 'update' : 'add'} contact${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, residentId, emergencyContacts, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Delete an emergency contact (with confirmation) */
  const handleDeleteContact = useCallback(async (contact) => {
    const name = [contact.contact_first_name, contact.contact_last_name].filter(Boolean).join(' ') || 'this contact';
    if (!window.confirm(`Are you sure you want to remove ${name}?`)) return;

    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      monitoring_contact_id: contact.monitoring_contact_id,
      campus_id: campusId,
      resident_id: parseInt(residentId),
      modified_by: session.userId,
    }];
    const result = await deleteEmergencyContact(payload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', `${name} has been removed.`);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to remove contact${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, residentId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Assign a device to the resident */
  const handleAssignDevice = useCallback(async (device) => {
    setSaving(true);
    const session = getSessionParams();
    const result = await assignDeviceToResident(device, residentId, session, areaId);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', `${device.device_name || 'Device'} has been assigned.`);
      setAssignDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to assign device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, areaId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Quick add: creates the device and assigns it to this resident in one call. */
  const handleEasyAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'device_add_resident', session);
    if (result && result[0]) {
      showToast('success', `${formData.device_name || 'Device'} added and assigned`);
      fetchBasicDataCycle();
    }
    return result;
  }, [getSessionParams, showToast, fetchBasicDataCycle]);

  /** Duplicate serial in the easy flow — assign the existing device instead of
   *  erroring, so the user never needs to know whether it was already added. */
  const handleEasyAssignExisting = useCallback(async (device) => {
    const session = getSessionParams();
    const result = await assignDeviceToResident(device, residentId, session, areaId);
    if (result && result[0]) {
      showToast('success', `${device.device_name || 'Device'} assigned`);
      fetchBasicDataCycle();
    }
    return result;
  }, [residentId, areaId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Remove device from resident */
  const handleRemoveDevice = useCallback(async () => {
    if (!removeDevice) return;
    setSaving(true);
    const session = getSessionParams();
    const result = await removeDeviceFromResident(removeDevice.device_id, residentId, session, removeDevice.device_name);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Device has been removed.');
      setRemoveDialogOpen(false);
      setRemoveDevice(null);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to remove device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [removeDevice, residentId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Relocate resident */
  const handleRelocate = useCallback(async (newAreaId) => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      resident_id: residentId,
      area_id: parseInt(newAreaId),
      modified_by: session.userId,
      vendor_ids: session.vendor_ids,
    }];
    const oldPayload = [{
      resident_id: residentId,
      area_id: areaId,
    }];

    const result = await relocateResident(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Resident relocated successfully.');
      setRelocateDialogOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during relocate${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, areaId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Move out resident */
  const handleMoveOut = useCallback(async () => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      resident_id: residentId,
      modified_by: session.userId,
      move_out_date: getUTCTimestamp(),
      vendor_ids: session.vendor_ids,
    }];
    const oldPayload = [{
      resident_id: residentId,
      area_id: areaId,
      is_active: 1,
    }];

    const result = await moveOutResident(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Resident has been moved out.');
      setMoveOutDialogOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during move out${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, areaId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Move in resident */
  const handleMoveIn = useCallback(async (newAreaId) => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      resident_id: residentId,
      area_id: parseInt(newAreaId),
      is_active: 1,
      modified_by: session.userId,
      campus_id: campusId,
      vendor_ids: session.vendor_ids,
      move_in_date: getUTCTimestamp(),
    }];
    const oldPayload = [{
      resident_id: residentId,
      area_id: areaId,
      is_active: 0,
    }];

    const result = await moveInResident(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Resident has been moved back in.');
      setMoveInDialogOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during move in${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, areaId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Delete resident */
  const handleDelete = useCallback(async () => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      resident_id: residentId,
      modified_by: session.userId,
      vendor_ids: session.vendor_ids,
    }];

    const result = await deleteResident(payload, session);
    setSaving(false);

    if (result && result[0]) {
      setDeleteDialogOpen(false);
      navigate('/community/residents');
    } else {
      showToast('error', `Error during delete${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [residentId, getSessionParams, showToast, navigate]);

  /** Add event note */
  const handleAddNote = useCallback(async (eventId, noteText) => {
    setSaving(true);
    const session = getSessionParams();
    const noteItems = [{
      event_id: eventId,
      campus_id: campusId,
      note: noteText,
      modified_by: getUserId(),
    }];

    const result = await addEventNote(noteItems, session);
    setSaving(false);

    if (result && result[0] === true) {
      showToast('success', 'Note added successfully.');
      setNoteDialogOpen(false);
      setNoteEventIndex(null);
      fetchReportingCycle();
    } else {
      showToast('error', `Error adding note${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, getSessionParams, showToast, fetchReportingCycle]);

  /** Open remove device dialog */
  const openRemoveDialog = useCallback((device) => {
    setRemoveDevice(device);
    setRemoveDialogOpen(true);
  }, []);

  /** Open note dialog */
  const openNoteDialog = useCallback((eventIndex) => {
    setNoteEventIndex(eventIndex);
    setNoteDialogOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const residentName = `${firstName} ${lastName}`;

  const bannerMeta = useMemo(() => [
    { label: 'Area Type', value: areaType || '—' },
    { label: 'Move-in', value: moveInDate && !['0000-00-00 00:00:00'].includes(moveInDate)
        ? convertIsoUTCToLocal(moveInDate, tzOffset)
        : '—' },
    { label: 'EHR ID', value: ehrId || '—' },
  ], [areaType, moveInDate, tzOffset, ehrId]);

  const bannerStatusDot = useMemo(() => {
    if (!isReview) return null;
    if (tagColor === '#e63757') return 'offline';
    if (tagColor === '#ff7815') return 'unknown';
    return 'unknown';
  }, [isReview, tagColor]);

  const bannerStatusLabel = useMemo(() => {
    if (!isReview) return '';
    return `${flagData?.review_index_name || 'High'} Volume Resident`;
  }, [isReview, flagData]);

  // --------------------------------------------------------------------------
  // RESIDENT TRENDS EPISODES
  // Fetched once here: open episodes render in the Basic Info panel, closed
  // ones on the Trend History tab. One request serves both.
  // --------------------------------------------------------------------------
  const loadTrendEpisodes = useCallback(async () => {
    if (!showTrends || !campusId || !residentId) {
      setTrendEpisodes([]);
      setTrendMutes([]);
      return;
    }
    const result = await fetchResidentEpisodes({
      campusId, residentId, status: 'all', limit: 200,
    });
    setTrendEpisodes(result.episodes || []);
    setTrendMutes(result.mutes || []);
  }, [showTrends, campusId, residentId]);

  useEffect(() => { loadTrendEpisodes(); }, [loadTrendEpisodes]);

  const openTrendEpisodes = useMemo(
    () => trendEpisodes.filter((e) => TREND_OPEN_STATUSES.includes(e.status)),
    [trendEpisodes],
  );
  const closedTrendEpisodes = useMemo(
    () => trendEpisodes.filter((e) => !TREND_OPEN_STATUSES.includes(e.status)),
    [trendEpisodes],
  );

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => {
    const t = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'reporting', label: 'Reporting' },
    ];
    if (isActive) {
      t.push({ key: 'devices', label: 'Devices' });
      t.push({ key: 'wellness', label: 'Wellness' });
    }
    // Only surfaces once the resident actually has closed trend concerns
    if (showTrends && closedTrendEpisodes.length > 0) {
      t.push({ key: 'trendhistory', label: 'Trend History' });
    }
    if (showAdminTabs) {
      t.push({ key: 'logs', label: 'Logs' });
    }
    return t;
  }, [isActive, showAdminTabs, showTrends, closedTrendEpisodes.length]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast ? (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>✕</button>
          </div>
        ) : null}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/community/residents" className="dp-breadcrumb__text">Residents</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{residentName.trim() || 'Resident Profile'}</span>
              </nav>
              <div className="dp-campus-row">
                <label htmlFor="rpp-campus-selector" className="dp-campus-label">Campus:</label>
                {campusList.length > 1 ? (
                  <select
                    id="rpp-campus-selector"
                    className="dp-campus-select"
                    value={campusId}
                    onChange={handleCampusChange}
                  >
                    {campusList.map((c) => (
                      <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="dp-campus-name">{campusName}</span>
                )}
              </div>
            </div>

            {/* Lifecycle action buttons */}
            {!loading ? (
              <div className="rpp-lifecycle-actions">
                {isActive ? (
                  <>
                    {editRights ? (
                      <button
                        className="dp-btn dp-btn--primary dp-btn--sm"
                        onClick={() => setRelocateDialogOpen(true)}
                      >
                        Relocate
                      </button>
                    ) : null}
                    {deleteRights ? (
                      <button
                        className="dp-btn dp-btn--danger dp-btn--sm"
                        onClick={() => setMoveOutDialogOpen(true)}
                      >
                        Move Out
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    {addRights ? (
                      <button
                        className="dp-btn dp-btn--primary dp-btn--sm"
                        onClick={() => setMoveInDialogOpen(true)}
                      >
                        Move Back In
                      </button>
                    ) : null}
                    {deleteRights && iAmHigherAuth(20, myRoleWeight) ? (
                      <button
                        className="dp-btn dp-btn--danger dp-btn--sm"
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={residentName.trim()}
          subtitle={areaName ? `Room: ${areaName}` : ''}
          statusDot={bannerStatusDot}
          statusLabel={bannerStatusLabel}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' ? (
            <BasicInfoTab
              firstName={firstName}
              lastName={lastName}
              nickName={nickName}
              phone={phone}
              email={email}
              notes={notes}
              areaName={areaName}
              areaType={areaType}
              areaId={areaId}
              locatingActive={locatingActive}
              locating={resLocation}
              imageUrl={imageUrl}
              stockPhoto={stockPhoto}
              isActive={isActive}
              moveInDate={moveInDate}
              moveOutDate={moveOutDate}
              resGroups={resGroups}
              ehrId={ehrId}
              ehrData={ehrData}
              basicData={basicData}
              isReview={isReview}
              reviewIndex={reviewIndex}
              tagColor={tagColor}
              flagData={flagData}
              tzOffset={tzOffset}
              loading={loading}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
              onPhotoUpload={() => setPhotoDialogOpen(true)}
              campusId={campusId}
              residentId={residentId}
              showTrends={showTrends}
              trendEpisodes={openTrendEpisodes}
              trendMutes={trendMutes}
              onTrendsChanged={loadTrendEpisodes}
              emergencyContacts={emergencyContacts}
              onAddContact={() => { setEditingContact(null); setContactDrawerOpen(true); }}
              onEditContact={(c) => { setEditingContact(c); setContactDrawerOpen(true); }}
              onDeleteContact={handleDeleteContact}
            />
          ) : null}

          {activeTab === 'reporting' ? (
            <ReportingTab
              hourData={hourData}
              weekData={weekData}
              reasonData={reasonData}
              eventData={eventData}
              eventNotesActive={eventNotesActive}
              isReasonEditable={isReasonEditable}
              isGrandstream={isGrandstream}
              tzOffset={tzOffset}
              loading={reportLoading}
              onAddNote={openNoteDialog}
              residentId={residentId}
              residentName={residentName}
            />
          ) : null}

          {activeTab === 'devices' && isActive ? (
            <DevicesTab
              resDeviceData={resDeviceData}
              residentName={residentName}
              devEditRights={devEditRights}
              onAssign={() => setAssignDrawerOpen(true)}
              onRemove={openRemoveDialog}
            />
          ) : null}

          {activeTab === 'wellness' && isActive ? (
            <WellnessTab
              careData={careData}
              wellnessWindowData={wellnessWindowData}
              resWellData={resWellData}
              resGroups={resGroups}
              areaGroups={areaGroups}
            />
          ) : null}

          {activeTab === 'trendhistory' && showTrends ? (
            <TrendHistoryTab episodes={closedTrendEpisodes} />
          ) : null}

          {activeTab === 'logs' && showAdminTabs ? (
            <LogsTab
              logData={logData}
              tzOffset={tzOffset}
            />
          ) : null}
        </div>

        {/* ---- Drawers ---- */}

        {/* Basic Info Drawer */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          firstName={firstName}
          lastName={lastName}
          nickName={nickName}
          phone={phone}
          email={email}
          notes={notes}
          ehrId={ehrId}
          moveInDate={moveInDate}
          tzOffset={tzOffset}
          offsiteActive={getOffsiteMonitoringVendors().some((v) => v.vendor_active)}
        />

        {/* Emergency Contact Drawer */}
        {contactDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => { setContactDrawerOpen(false); setEditingContact(null); }} aria-hidden="true" />
        ) : null}
        <EmergencyContactDrawer
          open={contactDrawerOpen}
          onClose={() => { setContactDrawerOpen(false); setEditingContact(null); }}
          onSave={handleSaveContact}
          saving={saving}
          contact={editingContact}
        />

        {/* Device Assign Drawer */}
        {assignDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setAssignDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <DeviceAssignDrawer
          open={assignDrawerOpen}
          onClose={() => setAssignDrawerOpen(false)}
          onAssign={handleAssignDevice}
          saving={saving}
          availableDevices={availableDevices}
          resDeviceData={resDeviceData}
          residentName={residentName}
          onAddNewDevice={() => setEasyAddOpen(true)}
        />

        {/* Simplified add-and-assign flow for brand-new devices. Rendered after
            the assign drawer on purpose: both sit at --z-modal, so DOM order is
            what puts this one on top. */}
        <EasyAddDeviceDrawer
          isOpen={easyAddOpen}
          onClose={() => setEasyAddOpen(false)}
          mode="resident"
          classPrefix="rp"
          roleWeight={myRoleWeight}
          target={{
            resident_id: residentId,
            resident_first_name: firstName,
            resident_last_name: lastName,
            area_id: areaId,
            area_name: areaName,
          }}
          existingDevices={availableDevices}
          campusId={campusId}
          sessionId={getSessionId()}
          onAdd={handleEasyAddDevice}
          onAssignExisting={handleEasyAssignExisting}
        />

        {/* ---- Dialogs ---- */}

        {/* Device Remove Dialog */}
        {removeDialogOpen && removeDevice ? (
          <DeviceRemoveDialog
            deviceName={removeDevice.device_name || removeDevice.manufacture_uuid || 'Device'}
            residentName={residentName}
            onClose={() => { setRemoveDialogOpen(false); setRemoveDevice(null); }}
            onConfirm={handleRemoveDevice}
            saving={saving}
          />
        ) : null}

        {/* Event Note Dialog */}
        {noteDialogOpen && noteEventIndex !== null ? (
          <EventNoteDialog
            event={eventData[noteEventIndex]}
            prenoteData={prenoteData}
            eventNotesPreform={eventNotesPreform}
            eventNotesFreeform={eventNotesFreeform}
            tzOffset={tzOffset}
            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
            onSave={handleAddNote}
            saving={saving}
          />
        ) : null}

        {/* Photo Upload Dialog */}
        {photoDialogOpen ? (
          <PhotoUploadDialog
            onClose={() => setPhotoDialogOpen(false)}
            onUpload={handlePhotoUpload}
            saving={saving}
            currentImage={imageUrl}
          />
        ) : null}

        {/* Relocate Dialog */}
        {relocateDialogOpen ? (
          <RelocateDialog
            currentAreaId={areaId}
            locationData={locationData}
            onClose={() => setRelocateDialogOpen(false)}
            onRelocate={handleRelocate}
            saving={saving}
          />
        ) : null}

        {/* Move Out Dialog */}
        {moveOutDialogOpen ? (
          <MoveOutDialog
            residentName={residentName}
            onClose={() => setMoveOutDialogOpen(false)}
            onConfirm={handleMoveOut}
            saving={saving}
          />
        ) : null}

        {/* Move In Dialog */}
        {moveInDialogOpen ? (
          <MoveInDialog
            residentName={residentName}
            locationData={locationData}
            onClose={() => setMoveInDialogOpen(false)}
            onConfirm={handleMoveIn}
            saving={saving}
          />
        ) : null}

        {/* Delete Dialog */}
        {deleteDialogOpen ? (
          <DeleteDialog
            residentName={residentName}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={handleDelete}
            saving={saving}
          />
        ) : null}
      </div>
      </div>
    </>
  );
};

export default ResidentProfilePage;
